import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, ExternalLink } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import RotatingText from '../components/common/RotatingText';
import GolfClubsMap from '../components/map/GolfClubsMap';
import { fetchGolfClubs } from '../services/clubsApi';

const ClubsPage = () => {
  const { isDarkMode } = useTheme();
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Load clubs from API
  useEffect(() => {
    const loadClubs = async () => {
      try {
        setLoading(true);
        const data = await fetchGolfClubs({ withCoordinates: true });
        setClubs(data);
      } catch (err) {
        console.error('Error loading clubs:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadClubs();
  }, []);

  const handleClubSelect = (club) => {
    setSelectedClub(club);
  };

  // Filter clubs based on search
  const filteredClubs = clubs.filter(club =>
    club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    club.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/clubsHero.png" 
            alt="Golf Clubs Directory" 
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90' 
              : 'bg-gradient-to-b from-black/40 via-black/50 to-black/70'
          }`}></div>
        </div>

        {/* Content */}
        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
  Our Clubs
</h1>
            
            {/* Rotating Text Section */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Discover</span>
              <RotatingText
                texts={[
                  'Excellence',
                  'Tradition',
                  'Community',
                  'Your Club',
                ]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? 'bg-fsgu-green-700/95 text-white'
                    : 'bg-fsgu-green-700 text-white'
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-120%', opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Explore {clubs.length} affiliated golf clubs across Free State
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section">
        <div className="container-custom">
          {/* Map Component */}
          <div className={`rounded-2xl overflow-hidden border ${
            isDarkMode
              ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
              : 'bg-fsgu-silver-50 border-fsgu-silver-200'
          }`}>
            {loading ? (
              <div className="aspect-video flex items-center justify-center">
                <div className="text-center">
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-fsgu-green-400 to-fsgu-green-600' 
                      : 'bg-gradient-to-br from-fsgu-green-500 to-fsgu-green-700'
                  }`}>
                    <MapPin className="w-6 h-6 text-fsgu-white animate-pulse" />
                  </div>
                  <p className={`text-sm font-medium ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-500'
                  }`}>
                    Loading golf clubs...
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="aspect-video flex items-center justify-center">
                <div className="text-center max-w-md px-4">
                  <MapPin className={`w-12 h-12 mx-auto mb-4 ${
                    isDarkMode ? 'text-fsgu-red-400' : 'text-fsgu-red-500'
                  }`} />
                  <p className={`text-sm font-medium mb-2 ${
                    isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-600'
                  }`}>
                    Unable to load map
                  </p>
                  <p className={`text-xs ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-500'
                  }`}>
                    {error}
                  </p>
                </div>
              </div>
            ) : (
              <div className="aspect-video">
                <GolfClubsMap
                  clubs={clubs}
                  selectedClub={selectedClub}
                  onClubSelect={handleClubSelect}
                  className="h-full w-full"
                />
              </div>
            )}
          </div>

          {/* Selected Club Info */}
          {selectedClub && (
            <div className={`mt-6 p-6 rounded-2xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              <h3 className={`text-xl font-bold mb-4 ${
                isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
              }`}>
                {selectedClub.name}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedClub.address && (
                  <div className="flex items-start space-x-3">
                    <MapPin className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <div>
                      <p className={`text-sm font-medium ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}>
                        {selectedClub.address}
                      </p>
                      {selectedClub.city && (
                        <p className={`text-sm ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          {selectedClub.city}, {selectedClub.postal_code}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {selectedClub.phone && (
                  <div className="flex items-start space-x-3">
                    <Phone className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <a
                      href={`tel:${selectedClub.phone}`}
                      className={`text-sm hover:underline ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}
                    >
                      {selectedClub.phone}
                    </a>
                  </div>
                )}

                {selectedClub.email && (
                  <div className="flex items-start space-x-3">
                    <Mail className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <a
                      href={`mailto:${selectedClub.email}`}
                      className={`text-sm hover:underline ${
                        isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                      }`}
                    >
                      {selectedClub.email}
                    </a>
                  </div>
                )}

                {selectedClub.holes && (
                  <div className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                  }`}>
                    ⛳ {selectedClub.holes} Holes
                    {selectedClub.par && ` • Par ${selectedClub.par}`}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Clubs Directory List */}
      <section className={`section ${
        isDarkMode ? 'bg-fsgu-gray-800/50' : 'bg-fsgu-silver-50'
      }`}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className={`text-3xl sm:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
            }`}>
              Club Directory
            </h2>

            <p className={`text-lg mb-8 ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              Browse all affiliated golf clubs
            </p>

            {/* Search Bar */}
            <div className="max-w-md mx-auto">
              <input
                type="text"
                placeholder="Search by club name or city..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full px-4 py-3 rounded-lg border ${
                  isDarkMode
                    ? 'bg-fsgu-gray-800 border-fsgu-gray-700 text-fsgu-silver-100 placeholder-fsgu-silver-500'
                    : 'bg-fsgu-white border-fsgu-silver-200 text-fsgu-silver-900 placeholder-fsgu-silver-400'
                } focus:outline-none focus:ring-2 focus:ring-fsgu-green-500`}
              />
            </div>
          </div>

          {/* Clubs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClubs.map((club) => (
              <div
                key={club.id}
                onClick={() => handleClubSelect(club)}
                className="card-hover cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className={`text-lg font-semibold ${
                    isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                  }`}>
                    {club.name}
                  </h3>
                  <ExternalLink className={`w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity ${
                    isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                  }`} />
                </div>

                {club.city && (
                  <div className="flex items-center space-x-2 mb-3">
                    <MapPin className={`w-4 h-4 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <p className={`text-sm ${
                      isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                    }`}>
                      {club.city}
                    </p>
                  </div>
                )}

                {club.phone && (
                  <div className="flex items-center space-x-2 mb-3">
                    <Phone className={`w-4 h-4 ${
                      isDarkMode ? 'text-fsgu-green-400' : 'text-fsgu-green-500'
                    }`} />
                    <a
                      href={`tel:${club.phone}`}
                      onClick={(e) => e.stopPropagation()}
                      className={`text-sm hover:underline ${
                        isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                      }`}
                    >
                      {club.phone}
                    </a>
                  </div>
                )}

                {club.holes && (
                  <p className={`text-sm ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                  }`}>
                    ⛳ {club.holes} Holes
                    {club.par && ` • Par ${club.par}`}
                  </p>
                )}
              </div>
            ))}
          </div>

          {filteredClubs.length === 0 && (
            <div className="text-center py-12">
              <p className={`text-lg ${
                isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
              }`}>
                No clubs found matching your search.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ClubsPage;